import React, { useEffect, useMemo, useState } from 'react';
import { PhoneOutgoing, ShieldCheck } from 'lucide-react';
import { readSession } from '../../auth/cognito';
import { useContact } from '../../context/ContactContext';
import { pickDialerTenant } from '../../lib/dialerTenant';

// Incident 2026-09-14 fix 1: optional props so the SAME dialer can be
// surfaced on an accepted callback task, pre-filled from the task's own
// attributes. With no props it behaves exactly as before (idle Waiting screen).
//   initialNumber / initialTenantId — pre-fill; re-applied when they change
//   dialContext — merged into the D8 dial context (callbackId, brandName…)
//   title / submitLabel / icon — copy overrides
export default function OutboundDialer({
  initialNumber = '',
  initialTenantId = '',
  dialContext = null,
  title = 'Make an outbound call',
  submitLabel = 'Call customer',
  icon: Icon = PhoneOutgoing,
} = {}) {
  const { handleDialNumber, handleEnd, dialPending, dialling, dialError, lastContactTenantId } = useContact();   // D37: dialling + handleEnd
  const tenants = useMemo(() => readSession()?.tenants || [], []);
  // Idle screen v3 (2026-09-19): pre-fill from context the console already
  // holds — task pre-fill, then the last contact's own tenant, then a sole
  // entitlement — never a build-time default (same rule as D26 / PR #84).
  const prefill = pickDialerTenant({ initialTenantId, lastContactTenantId, entitled: tenants });
  const defaultTenant = prefill.tenantId;
  const [tenantId, setTenantId] = useState(defaultTenant);
  const [number, setNumber] = useState(initialNumber || '');
  // 2026-09-17: one-click dial had no confirmation at all — a wrong tenant
  // selected or a fat-fingered number placed a real call to a real customer
  // immediately. First submit shows what's about to be dialled; a second,
  // explicit confirm actually places it. Editing tenant or number after
  // confirming is shown resets it — never confirm stale values.
  const [confirming, setConfirming] = useState(false);

  // A new task (new pre-fill) resets the fields; typing is otherwise kept.
  useEffect(() => { setNumber(initialNumber || ''); setConfirming(false); }, [initialNumber]);
  useEffect(() => { if (defaultTenant) setTenantId(defaultTenant); }, [defaultTenant]);

  const submit = event => {
    event.preventDefault();
    if (!confirming) { setConfirming(true); return; }
    handleDialNumber(number, { ...(dialContext || {}), tenantId });
    setConfirming(false);
  };

  return (
    <form className="outbound-dialer" onSubmit={submit}>
      <div className="outbound-dialer-title">
        <Icon size={15} />
        <span>{title}</span>
      </div>
      <label>
        Tenant
        <select
          value={tenantId}
          onChange={event => { setTenantId(event.target.value); setConfirming(false); }}
          required
        >
          <option value="" disabled>Choose tenant</option>
          {tenants.map(tenant => <option key={tenant} value={tenant}>{tenant}</option>)}
        </select>
        {/* A pre-selection from the last contact is a guess about the next
            call, so say where it came from while it is still the value. */}
        {prefill.source === 'last-contact' && tenantId === prefill.tenantId && (
          <span className="outbound-dialer-prefill">From your last contact — change if needed</span>
        )}
      </label>
      <label>
        Customer number
        <input
          type="tel"
          value={number}
          onChange={event => { setNumber(event.target.value); setConfirming(false); }}
          placeholder="+61…"
          autoComplete="tel"
          required
        />
      </label>
      {confirming && !dialPending && !dialling && (
        <p className="outbound-dialer-confirm" role="alert">
          <ShieldCheck size={13} />
          Confirm: call <strong>{number}</strong> as <strong>{tenantId}</strong>?
        </p>
      )}
      <button type="submit" disabled={dialPending || dialling || !tenantId || !number.trim()}>
        <Icon size={13} />
        {dialPending ? 'Preparing call…' : dialling ? 'Dialling…' : confirming ? 'Confirm & call' : submitLabel}
      </button>
      {confirming && !dialPending && !dialling && (
        <button type="button" className="outbound-dialer-cancel" onClick={() => setConfirming(false)}>
          Cancel
        </button>
      )}
      {/* D37 (2026-09-16): the far end is ringing — let the agent drop it from
          here as well as from the rail's End control. */}
      {dialling && (
        <button type="button" className="outbound-dialer-hangup" onClick={handleEnd} title="Hang up before the call is answered">
          Hang up
        </button>
      )}
      {tenants.length === 0 && (
        <p className="outbound-dialer-error" role="alert">Your console account has no permitted tenants.</p>
      )}
      {dialError && <p className="outbound-dialer-error" role="alert">{dialError}</p>}
    </form>
  );
}
